import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
} from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { provideSharedTranslocoScope } from '@jfw/angular/shared/i18n';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { Language, LanguageSchema, SUPPORTED_LANGUAGES } from '@jfw/types';

/**
 * EXT-5699: language picker shared by Platform Portal and (later) Dealer Portal.
 *
 * The component is app-agnostic: it never loads or saves the user's
 * preference itself. The host passes the current language in and handles
 * `languageChange` with its own persistence call (e.g. Platform Portal's
 * `PUT /api/2/platformUser/language`, EXT-5698).
 *
 * On a selection it switches Transloco's active language *before* emitting,
 * so the UI updates instantly whether or not the host's save succeeds.
 *
 * The host must configure Transloco with `reRenderOnLangChange: true` (as
 * jfw-platform-portal does); otherwise Transloco's default leaves this
 * component's own label in the previous language after a switch.
 *
 * It does not push the `language` input into Transloco. Applying a saved
 * preference at sign-in is the host app's job, so rendering the switcher never
 * changes the language as a side effect.
 *
 * @example
 * <app-language-switcher
 *   [language]="user().language ?? defaultLanguage"
 *   (languageChange)="saveLanguage($event)"
 * />
 */
@Component({
  selector: 'app-language-switcher',
  templateUrl: './language-switcher.component.html',
  styleUrl: './language-switcher.component.scss',
  imports: [MatFormFieldModule, MatSelectModule, TranslocoDirective],
  // Registered here as well as at app level so the component resolves its own
  // labels in any host, even one that hasn't added the shared scope yet.
  providers: [provideSharedTranslocoScope()],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LanguageSwitcherComponent {
  private readonly transloco = inject(TranslocoService);

  /** The user's current language, as known to the host app. */
  readonly language = input.required<Language>();

  /** Emits the newly selected language, after Transloco has switched to it. */
  readonly languageChange = output<Language>();

  protected readonly languages = SUPPORTED_LANGUAGES;

  protected onSelectionChange(event: MatSelectChange): void {
    const parsed = LanguageSchema.safeParse(event.value);
    if (!parsed.success) return;

    this.transloco.setActiveLang(parsed.data);
    this.languageChange.emit(parsed.data);
  }
}
